import { Component, OnDestroy } from '@angular/core';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { FormlyFieldProps } from '@ngx-formly/primeng/form-field';
import { TreeNode } from 'primeng/api';
import { Tree } from 'primeng/tree';
import { Checkbox } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { FORM_IMPORTS } from '../../_shared/_imports/form.import';

export interface CheckboxTreeFieldProps extends FormlyFieldProps {
  options: TreeNode[];
  filter?: boolean;
  filterPlaceholder?: string;
  selectAll?: boolean;
}

@Component({
  selector: 'checkbox-tree-field',
  standalone: true,
  imports: [
    ...FORM_IMPORTS,
    Tree,
    Checkbox,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
  ],
  template: `
    <div class="flex flex-col gap-2">
      @if (props.filter || props.selectAll) {
        <div class="flex items-center gap-2 pl-3">
          @if (props.selectAll) {
            <p-checkbox
              [(ngModel)]="selectAllChecked"
              [binary]="true"
              (onChange)="onSelectAllChange($event.checked)"
            />
          }
          @if (props.filter) {
            <p-iconfield iconPosition="right" class="w-full">
              <input
                pInputText
                class="w-full"
                [placeholder]="props.filterPlaceholder ?? ''"
                [ngModel]="filterValue"
                (ngModelChange)="onFilterInput($event)"
              />
              <p-inputicon>
                <i class="fas fa-search"></i>
              </p-inputicon>
            </p-iconfield>
          }
        </div>
      }
      <p-tree
        [value]="filteredOptions"
        selectionMode="checkbox"
        [(selection)]="selection"
        (onNodeSelect)="onTreeSelectionChanged()"
        (onNodeUnselect)="onTreeSelectionChanged()"
        class="w-full p-0"
        scrollHeight="flex"
      />
    </div>
  `,
})
export class CheckboxTreeFieldType
  extends FieldType<FieldTypeConfig<CheckboxTreeFieldProps>>
  implements OnDestroy
{
  selectAllChecked = false;
  filterValue = '';
  filteredOptions: TreeNode[] = [];
  selection: TreeNode[] = [];

  private _totalLeafCount = 0;
  private _filterTimer: ReturnType<typeof setTimeout> | null = null;
  private _updating = false;

  ngOnInit(): void {
    this.filteredOptions = this.props.options ?? [];
    this._totalLeafCount = this._countLeaves(this.filteredOptions);
    if (this.formControl.value) {
      this.selection = this.formControl.value;
      this._applyPartialSelection(this.props.options ?? [], this.selection);
    }
    this.formControl.valueChanges.subscribe((value: TreeNode[]) => {
      if (this._updating) return;
      // Guard the whole external-write reaction, not just our own click
      // handler: `selection` is a signal-based model() on p-tree, and
      // reassigning it here is enough on its own to make its OnPush view
      // re-check (no need to also force a new `filteredOptions`/`value`
      // reference — that extra simultaneous input change was replaying
      // into a reentrant onNodeSelect/onNodeUnselect -> onTreeSelectionChanged
      // -> formControl write cycle and blowing the call stack).
      this._updating = true;
      this.selection = value ?? [];
      this._applyPartialSelection(this.props.options ?? [], this.selection);
      this._syncSelectAll();
      this._updating = false;
    });
  }

  ngOnDestroy(): void {
    if (this._filterTimer) clearTimeout(this._filterTimer);
  }

  onTreeSelectionChanged(): void {
    if (this._updating) return;
    this._updating = true;
    this.formControl.setValue(this.selection, { emitEvent: false });
    this.formControl.markAsDirty();
    this._syncSelectAll();
    this._updating = false;
  }

  /**
   * p-tree only computes a group node's indeterminate `partialSelected`
   * state as a side effect of its own click-driven propagateUp/Down —
   * a selection set externally (restoring from localStorage, applying a
   * preset) bypasses that entirely, leaving partially-selected groups
   * looking fully unchecked. Recompute it by hand from the tree + the new
   * selection whenever that happens.
   */
  private _applyPartialSelection(nodes: TreeNode[], selection: TreeNode[]): void {
    const selected = new Set(selection);
    const walk = (list: TreeNode[]): boolean => {
      let anySelected = false;
      for (const node of list) {
        if (node.children?.length) {
          const childAnySelected = walk(node.children);
          const fullySelected = selected.has(node);
          node.partialSelected = !fullySelected && childAnySelected;
          if (fullySelected || childAnySelected) anySelected = true;
        } else if (selected.has(node)) {
          anySelected = true;
        }
      }
      return anySelected;
    };
    walk(nodes);
  }

  private _syncSelectAll(): void {
    if (!this.props.selectAll) return;
    const selectedLeafCount = (this.selection ?? []).filter(
      (n: TreeNode) => !n.children?.length,
    ).length;
    this.selectAllChecked =
      this._totalLeafCount > 0 && selectedLeafCount >= this._totalLeafCount;
  }

  onSelectAllChange(checked: boolean): void {
    this._updating = true;
    this.selection = checked ? this._collectAll(this.filteredOptions) : [];
    this.formControl.setValue(this.selection, { emitEvent: false });
    this.formControl.markAsDirty();
    this._updating = false;
  }

  onFilterInput(value: string): void {
    this.filterValue = value;
    if (this._filterTimer) clearTimeout(this._filterTimer);
    if (!value.trim()) {
      this.filteredOptions = this.props.options ?? [];
      return;
    }
    this._filterTimer = setTimeout(() => this._applyFilter(value), 200);
  }

  private _applyFilter(value: string): void {
    const options = this.props.options ?? [];
    if (!value.trim()) {
      this.filteredOptions = options;
      return;
    }
    const lower = value.toLowerCase();
    this.filteredOptions = this._filterTree(options, lower);
  }

  private _filterTree(nodes: TreeNode[], query: string): TreeNode[] {
    return nodes
      .map((node) => {
        if (!node.children?.length) {
          return node.label?.toLowerCase().includes(query) ? node : null;
        }
        const filtered = this._filterTree(node.children, query);
        return filtered.length > 0
          ? { ...node, children: filtered, expanded: true }
          : null;
      })
      .filter(Boolean) as TreeNode[];
  }

  private _countLeaves(nodes: TreeNode[]): number {
    return nodes.reduce(
      (sum, n) =>
        n.children?.length ? sum + this._countLeaves(n.children) : sum + 1,
      0,
    );
  }

  private _collectAll(nodes: TreeNode[]): TreeNode[] {
    return nodes.flatMap((n) =>
      n.children?.length ? [n, ...this._collectAll(n.children)] : [n],
    );
  }
}
